import React from "react";
import { Link, useRouteMatch } from "react-router-dom";
import classNames from "classnames";

const StatusStep = ({ label, status }) => {
  const isCompleted = status === "completed";
  const isProcessing = status === "processing" || status === "running";
  const isFailed = status === "failed";
  const isPending = status === "pending";

  const iconClasses = classNames(
    "flex items-center justify-center w-6 h-6 rounded-full text-xs mr-3 border",
    {
      "bg-green-500 border-green-500 text-white": isCompleted,
      "bg-blue-500 border-blue-500 text-white animate-pulse": isProcessing,
      "bg-red-500 border-red-500 text-white": isFailed,
      "bg-gray-100 border-gray-300 text-gray-400": isPending,
    }
  );

  const textClasses = classNames("text-sm", {
    "font-bold text-gray-900": isProcessing,
    "text-gray-500": isPending,
    "text-gray-700": isCompleted || isFailed,
  });

  const renderIcon = () => {
    if (isCompleted) return "✓";
    if (isFailed) return "✕";
    if (isProcessing) return "⟳";
    return "";
  };

  return (
    <div className="flex items-center py-2">
      <div className={iconClasses}>{renderIcon()}</div>
      <span className={textClasses}>{label}</span>
    </div>
  );
};

const ResyncStatus = ({ steps, jobStatus, jobId, detectOrphans }) => {
  const isAdminRoute = useRouteMatch("/admin/shelterluv/resync");
  const jobsBasePath = isAdminRoute
    ? "/admin/shelterluv/resync-jobs"
    : "/api-management/resync-jobs";

  if (!steps) return null;

  const baseStepsCompleted =
    steps.animalData === "completed" &&
    steps.eventData === "completed" &&
    steps.pairing === "completed";

  const orphanStepCompleted =
    !detectOrphans || steps.orphanDetection === "completed";

  const isJobCompleted = jobStatus === "completed";
  const isJobFailed = jobStatus === "failed";
  const isJobRunning =
    !isJobCompleted && !isJobFailed && jobStatus && jobStatus !== "pending";

  return (
    <div className="mt-8 border-t pt-6">
      <h3 className="font-semibold text-lg mb-4">Progress</h3>

      <div className="space-y-2 bg-gray-50 p-4 rounded border">
        <StatusStep
          label="Resyncing Animal Data..."
          status={steps.animalData || "pending"}
        />
        <StatusStep
          label="Resyncing Event Data..."
          status={steps.eventData || "pending"}
        />
        <StatusStep
          label="Syncing Paired Records..."
          status={steps.pairing || "pending"}
        />
        {detectOrphans && (
          <StatusStep
            label="Detecting Orphaned Animals..."
            status={steps.orphanDetection || "pending"}
          />
        )}
      </div>

      {isJobCompleted && (
        <div className="mt-4 space-y-3">
          <div className="p-4 bg-green-50 text-green-800 rounded border border-green-200">
            <strong>✓ Success!</strong> Resync completed successfully. Please
            reaggregate your data now to update your monthly totals.
            {detectOrphans && (
              <span className="block mt-1">
                Orphaned animals have been flagged for cleanup. Review them in
                the{" "}
                <Link
                  to={
                    isAdminRoute
                      ? "/admin/cleanup/merged-duplicate-events"
                      : "/api-management/orphaned-animals"
                  }
                  className="underline font-medium"
                >
                  Orphaned Animals Cleanup
                </Link>{" "}
                tool.
              </span>
            )}
          </div>
        </div>
      )}

      {isJobFailed && (
        <div className="mt-4 space-y-3">
          <div className="p-4 bg-red-50 text-red-800 rounded border border-red-200">
            <strong>✕ Error!</strong> An error occurred during the resync
            process. Please check the job details and try again.
          </div>
        </div>
      )}

      {isJobRunning && (
        <div className="mt-4 p-4 bg-blue-50 text-blue-800 rounded border border-blue-200">
          <strong>⟳ Processing...</strong> Resync job is currently running.
          Please wait while we process your data.
        </div>
      )}

      {jobStatus && (isJobCompleted || isJobFailed) && jobId && (
        <div className="mt-4 flex justify-end">
          <Link
            to={`${jobsBasePath}/${jobId}`}
            className={classNames(
              "inline-flex items-center px-4 py-2 text-white rounded transition-colors",
              {
                "bg-blue-600 hover:bg-blue-700": isJobCompleted,
                "bg-red-600 hover:bg-red-700": isJobFailed,
              }
            )}
          >
            View Job Details
          </Link>
        </div>
      )}
    </div>
  );
};

export default ResyncStatus;
